import { useContext, useEffect, useState } from "react";
import { FiFilter, FiPlus, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import InfiniteScroll from "react-infinite-scroll-component";
import { CommonTable, Loader } from "../../Components/Common";
import SearchableDropdown from "../../Components/Common/SearchableDropdown";
import {
  getBackdatedPermissions,
  createBackdatedPermission,
  deleteBackdatedPermission,
} from "../../Services/attendanceService";
import { getAllUsers } from "../../Services/UserService";
import { GET_ALL_PAGINATION, RESET_PAGINATION, USER_ROLES } from "../../Constants/Strings";
import { getUniqueDataById, formatDateDDMMYYYY } from "../../Utils";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { UserContext } from "../../Context";
import BackdatedPermissionMobileCard from "./Component/BackdatedPermissionMobileCard";

const TABLE_COLUMNS = [
  "user_name",
  "start_date",
  "end_date",
  "reason",
  "granted_by_name",
  "actions",
];

const COLUMN_LABELS = {
  user_name: "User",
  start_date: "Start Date",
  end_date: "End Date",
  reason: "Reason",
  granted_by_name: "Granted By",
  actions: "Actions",
};

const BackdatedPermissionList = () => {
  const { user } = useContext(UserContext);
  const isHR = user?.role === USER_ROLES.HR;

  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);

  const [showFilter, setShowFilter] = useState(false);
  const [selectedFilterUser, setSelectedFilterUser] = useState(null);
  const [filters, setFilters] = useState({ user: "" });

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedGrantUser, setSelectedGrantUser] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchUsersForDropdown = async ({ search }) => {
    try {
      const response = await getAllUsers({ ...GET_ALL_PAGINATION, search });
      if (response.status === 200) {
        return response.data?.results || [];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const fetchPermissions = async (finalParams, isInitial = false) => {
    setLoading(true);
    try {
      const response = await getBackdatedPermissions({ ...finalParams });
      if (response.status === 200) {
        if (isInitial) {
          setPermissions(response.data.results || []);
        } else {
          setPermissions((prevData) =>
            getUniqueDataById(prevData, response.data.results),
          );
        }
        setPagination((prevData) => ({
          ...prevData,
          count: response.data.count,
          offset: prevData.offset + prevData.limit,
        }));
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || "Failed to fetch backdated approvals");
    } finally {
      setLoading(false);
    }
  };

  const getPaginationData = () => {
    if (pagination.offset > pagination.count) return;
    fetchPermissions({ ...pagination, ...filters });
  };

  useEffect(() => {
    fetchPermissions({ ...RESET_PAGINATION, ...filters }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetAddForm = () => {
    setSelectedGrantUser(null);
    setStartDate("");
    setEndDate("");
    setReason("");
  };

  const handleAddPermission = async () => {
    if (!selectedGrantUser?.id) {
      toast.error("Please select a user");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    if (endDate < startDate) {
      toast.error("End date cannot be before start date");
      return;
    }

    setSubmitting(true);
    try {
      const response = await createBackdatedPermission({
        user: selectedGrantUser.id,
        start_date: startDate,
        end_date: endDate,
        reason,
      });
      if (response.status === 201) {
        toast.success("Backdated attendance approval granted");
        setShowAddModal(false);
        resetAddForm();
        setPermissions([]);
        fetchPermissions({ ...RESET_PAGINATION, ...filters }, true);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || "Failed to grant approval");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Revoke this backdated attendance approval?")) return;
    try {
      const response = await deleteBackdatedPermission(id);
      if (response.status === 204) {
        toast.success("Approval revoked");
        setPermissions((prev) => prev.filter((item) => item.id !== id));
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || "Failed to revoke approval");
    }
  };

  const renderField = (key, row) => {
    if (key === "start_date" || key === "end_date") {
      return formatDateDDMMYYYY(row[key]) || row[key];
    }
    if (key === "reason") {
      return row.reason || "-";
    }
    if (key === "granted_by_name") {
      return row.granted_by_name || "-";
    }
    if (key === "actions") {
      if (!isHR) return null;
      return (
        <button
          className="text-red-500 hover:text-red-700 p-1"
          title="Revoke"
          onClick={() => handleDelete(row.id)}
        >
          <FiTrash2 className="text-lg" />
        </button>
      );
    }
    return row[key];
  };

  return (
    <div className="px-4 py-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">
          Backdated Attendance Approvals
        </h2>

        {isHR && (
          <div className="flex gap-2">
            <button
              className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
              onClick={() => setShowFilter(true)}
            >
              <FiFilter className="text-xl" />
            </button>
            <button
              className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
              onClick={() => setShowAddModal(true)}
            >
              <FiPlus className="text-xl" />
            </button>
          </div>
        )}
      </div>

      {showFilter && (
        <div className="inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center absolute">
          <div className="bg-white w-full max-w-md mx-4 rounded-2xl shadow-xl p-6 relative">
            <h3 className="text-lg font-semibold text-primary mb-4">
              Filter Approvals
            </h3>
            <div className="flex flex-col gap-4">
              <SearchableDropdown
                label="User"
                fetchFunction={fetchUsersForDropdown}
                valueKey="id"
                labelKey="full_name"
                placeholder="All Users"
                search_box_placeholder="Search User"
                includeAllOption
                allOptionLabel="All Users"
                value={selectedFilterUser}
                onSelect={(selected) => {
                  setSelectedFilterUser(selected);
                  setFilters((prev) => ({ ...prev, user: selected?.id || "" }));
                }}
              />
              <div className="flex justify-between items-center mt-4">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm"
                  onClick={() => {
                    setSelectedFilterUser(null);
                    setFilters({ user: "" });
                    setPermissions([]);
                    fetchPermissions({ ...RESET_PAGINATION, user: "" }, true);
                  }}
                >
                  Clear
                </button>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm text-gray-800"
                    onClick={() => setShowFilter(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-primary hover:bg-green-600 text-white text-sm"
                    onClick={() => {
                      setPermissions([]);
                      fetchPermissions({ ...RESET_PAGINATION, ...filters }, true);
                      setShowFilter(false);
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center absolute">
          <div className="bg-white w-full max-w-md mx-4 rounded-2xl shadow-xl p-6 relative">
            <h3 className="text-lg font-semibold text-primary mb-4">
              Grant Backdated Attendance Approval
            </h3>
            <div className="flex flex-col gap-4">
              <SearchableDropdown
                label="User"
                fetchFunction={fetchUsersForDropdown}
                valueKey="id"
                labelKey="full_name"
                placeholder="Select User"
                search_box_placeholder="Search User"
                value={selectedGrantUser}
                onSelect={(selected) => setSelectedGrantUser(selected)}
              />

              <div className="flex gap-2">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    max={endDate || undefined}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="block text-sm font-medium text-gray-700">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Reason (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm text-gray-800"
                  onClick={() => {
                    setShowAddModal(false);
                    resetAddForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-primary hover:bg-green-600 text-white text-sm disabled:opacity-50"
                  disabled={submitting}
                  onClick={handleAddPermission}
                >
                  {submitting ? "Saving..." : "Grant Approval"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && permissions.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Loader size={4} />
        </div>
      ) : (
        <>
          {/* Desktop table view */}
          <div className="hidden md:block">
            <InfiniteScroll
              dataLength={permissions.length}
              next={getPaginationData}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="backdatedPermissionScrollDiv"
            >
              <div
                className="h-[70vh] overflow-x-auto overflow-y-auto border rounded-md"
                id="backdatedPermissionScrollDiv"
              >
                <CommonTable
                  tableColumns={
                    isHR
                      ? TABLE_COLUMNS
                      : TABLE_COLUMNS.filter((col) => col !== "actions")
                  }
                  columnLabels={COLUMN_LABELS}
                  tableRows={permissions}
                  renderField={renderField}
                  headerClassName="bg-gray-100"
                />
              </div>
            </InfiniteScroll>
          </div>

          {/* Mobile card view */}
          <div className="block md:hidden">
            <InfiniteScroll
              dataLength={permissions.length}
              next={getPaginationData}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="backdatedPermissionMobileScrollDiv"
            >
              <div
                className="h-[70vh] overflow-y-auto space-y-3"
                id="backdatedPermissionMobileScrollDiv"
              >
                {permissions.length === 0 ? (
                  <div className="text-center text-gray-500 pt-10">
                    No records found
                  </div>
                ) : (
                  permissions.map((item) => (
                    <BackdatedPermissionMobileCard
                      key={item.id}
                      data={item}
                      isHR={isHR}
                      onDelete={handleDelete}
                    />
                  ))
                )}
              </div>
            </InfiniteScroll>
          </div>
        </>
      )}
    </div>
  );
};

export default BackdatedPermissionList;
